import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Autocomplete,
  Box,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";

/**
 * Shared types used by the Vendor Allocation section.
 * Import these from this file wherever vendor split state is managed
 * (e.g. CreateTransferOrder.tsx, CreateSalesOrder.tsx) instead of
 * redeclaring them locally.
 */
export interface VendorSplitEntry {
  id: number;
  vendorId: string;
  vendorName: string;
  quantity: number;
  unitPrice: number;
}

export interface VendorOption {
  productId: number;
  vendorId: number;
  quantity: number;
  vendorName: string;
}

export interface VendorAllocationProps {
  index: number;
  productId: number;
  quantity: number;
  price: number;
  tax: number;
  vendorSplits: VendorSplitEntry[];
  vendorAllocationOptions: VendorOption[];
  isLoadingVendorOptions?: boolean;
  onAddVendorSplitRow: (index: number) => void;
  onRemoveVendorSplitRow: (index: number, splitId: number) => void;
  onVendorSplitFieldChange: (
    index: number,
    splitId: number,
    field: "vendorId" | "vendorName" | "quantity",
    value: string | number
  ) => void;
  onCancel: (index: number) => void;
}

const VendorAllocation: React.FC<VendorAllocationProps> = ({
  index,
  productId,
  quantity,
  price,
  tax,
  vendorSplits,
  vendorAllocationOptions,
  isLoadingVendorOptions,
  onAddVendorSplitRow,
  onRemoveVendorSplitRow,
  onVendorSplitFieldChange,
  onCancel,
}) => {
  const totalAllocated = vendorSplits.reduce(
    (sum, split) => sum + (Number(split.quantity) || 0),
    0
  );
  const isFullyAllocated = totalAllocated === (quantity || 0);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const firstVendorSplit = vendorSplits[0];

  const handleQuantityChange = (splitId: number, value: string) => {
    const enteredQuantity = Number(value);
    const isNewAllocation = splitId !== firstVendorSplit?.id;
    const firstAllocationIsUnchanged =
      Number(firstVendorSplit?.quantity) === (quantity || 0);
    const quantityAllocatedToOtherVendors = vendorSplits
      .filter((split) => split.id !== splitId)
      .reduce((sum, split) => sum + (Number(split.quantity) || 0), 0);
    const maximumAllowedQuantity = Math.max(
      0,
      (quantity || 0) - quantityAllocatedToOtherVendors
    );

    if (!Number.isFinite(enteredQuantity) || enteredQuantity < 0) {
      setSnackbarMessage("Quantity must be zero or greater.");
      setSnackbarOpen(true);
      return;
    }

    if (
      isNewAllocation &&
      enteredQuantity > 0 &&
      firstAllocationIsUnchanged
    ) {
      setSnackbarMessage(
        "This is the maximum quantity. Please change the quantity in the first vendor allocation first."
      );
      setSnackbarOpen(true);
      return;
    }

    if (enteredQuantity > maximumAllowedQuantity) {
      setSnackbarMessage(
        `Quantity cannot exceed the remaining available quantity (${maximumAllowedQuantity}).`
      );
      setSnackbarOpen(true);
      return;
    }

    onVendorSplitFieldChange(index, splitId, "quantity", enteredQuantity);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        p: 2.5,
      }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "#1366D9",
          }}>
          Vendor Allocation
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: isFullyAllocated ? "#2E7D32" : "#F57C00",
            }}
          />
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: isFullyAllocated ? "#2E7D32" : "#F57C00",
              fontWeight: 500,
            }}>
            Total Allocated: {totalAllocated} / {quantity || 0}
          </Typography>
        </Box>
      </Box>

      {vendorSplits.map((split) => (
        <Box
          key={split.id}
          sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
          <Autocomplete
            size="small"
            options={vendorAllocationOptions.filter(
              (option) =>
                option.productId === productId &&
                !vendorSplits.some(
                  (s) =>
                    s.id !== split.id &&
                    s.vendorId === option.vendorId.toString()
                )
            )}
            getOptionLabel={(option) => option.vendorName || ""}
            value={
              vendorAllocationOptions.find(
                (v) => v.vendorId.toString() === split.vendorId
              ) || null
            }
            loading={isLoadingVendorOptions}
            onChange={(_, newValue) => {
              onVendorSplitFieldChange(
                index,
                split.id,
                "vendorId",
                newValue ? newValue.vendorId.toString() : ""
              );
              onVendorSplitFieldChange(
                index,
                split.id,
                "vendorName",
                newValue ? newValue.vendorName : ""
              );
            }}
            sx={{ flex: 2, minWidth: 240 }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Select vendor"
                InputProps={{
                  ...params.InputProps,
                  style: { fontSize: "0.875rem" },
                }}
              />
            )}
          />
          <TextField
            size="small"
            type="number"
            value={split.quantity || ""}
            onChange={(e) => handleQuantityChange(split.id, e.target.value)}
            inputProps={{
              min: 0,
              max: Math.max(
                0,
                (quantity || 0) -
                  vendorSplits
                    .filter((otherSplit) => otherSplit.id !== split.id)
                    .reduce(
                      (sum, otherSplit) =>
                        sum + (Number(otherSplit.quantity) || 0),
                      0
                    )
              ),
            }}
            InputProps={{ style: { fontSize: "0.875rem" } }}
            sx={{ width: 90 }}
          />
          <Typography sx={{ width: 90, fontSize: "0.8rem", color: "#9E9E9E" }}>
            ₹{(price || 0).toFixed(2)}
          </Typography>

          <Typography sx={{ width: 70, fontSize: "0.8rem", color: "#9E9E9E" }}>
            {tax || 0}
          </Typography>

          <Typography
            sx={{
              width: 110,
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#1E1E1E",
            }}>
            ₹
            {(
              (split.quantity || 0) *
              (price || 0) *
              (1 + (tax || 0) / 100)
            ).toFixed(2)}
          </Typography>

          <IconButton
            onClick={() => onRemoveVendorSplitRow(index, split.id)}
            disabled={vendorSplits.length <= 1}
            sx={{
              color: vendorSplits.length <= 1 ? "#F3B0A9" : "#FF0000",
            }}
            size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <Typography
          onClick={() => onAddVendorSplitRow(index)}
          sx={{
            color: "#1366D9",
            fontSize: "0.875rem",
            cursor: "pointer",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
          }}>
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
          Add More Vendor
        </Typography>
        <Typography
          onClick={() => onCancel(index)}
          sx={{
            color: "#9E9E9E",
            fontSize: "0.8rem",
            cursor: "pointer",
            ml: 3,
          }}>
          Cancel
        </Typography>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorAllocation;
